// StockManagement.jsx - FIXED: Uses /stock/ endpoint for real-time stock
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../apiClient";

// ==========================================
// STOCK ALERT THRESHOLDS (Outside component)
// ==========================================
const ALERT_THRESHOLDS = {
  "Flour": { threshold: 15, unit: "kg" },
  "Oil": { threshold: 15, unit: "l" },
  "Milk": { threshold: 10, unit: "l" }
};

const StockManagement = () => {
  // ==========================================
  // STATE MANAGEMENT
  // ==========================================
  const [ingredients, setIngredients] = useState([]);
  const [supplies, setSupplies] = useState([]); // kept only for supply history tab
  const [stockData, setStockData] = useState([]); // NEW: real-time stock from /stock/
  const [activeTab, setActiveTab] = useState("supplies"); // "supplies" | "ingredients"
  
  // Form states
  const [newSupply, setNewSupply] = useState({ 
    ingredient_id: "", 
    amount: "", 
    notes: "" 
  });
  const [newIngredient, setNewIngredient] = useState({ 
    name: "", 
    unit: "kg", 
    cost_pln: "" 
  });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ 
    name: "", 
    unit: "kg", 
    cost_pln: "" 
  });
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const userRole = localStorage.getItem("userRole");
  const navigate = useNavigate();

  // ==========================================
  // HELPER FUNCTIONS
  // ==========================================
  const plnToGroszy = (pln) => Math.round(parseFloat(pln || 0) * 100);
  const groszyToPln = (groszy) => (groszy / 100).toFixed(2);

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(null), 5000);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ==========================================
  // DERIVED DATA FROM /stock/
  // ==========================================
  const stockMap = useMemo(() => {
    const map = {};
    stockData.forEach(item => {
      map[item.id] = {
        name: item.name,
        unit: item.unit,
        currentStock: item.available,
        isLow: false,
        threshold: ALERT_THRESHOLDS[item.name]?.threshold || null
      };
    });

    // Apply low stock logic
    Object.values(map).forEach(item => {
      if (item.threshold && item.currentStock < item.threshold) {
        item.isLow = true;
      }
    });

    return map;
  }, [stockData]);

  const lowStockItems = useMemo(() => {
    return Object.values(stockMap).filter(item => item.isLow);
  }, [stockMap]);

  const getStockColor = (ingredientId) => {
    const stock = stockMap[ingredientId];
    if (!stock) return "bg-gray-100";
    
    if (stock.isLow) return "bg-red-100 border-red-300";
    if (stock.threshold && stock.currentStock < stock.threshold * 1.5) return "bg-yellow-100 border-yellow-300";
    return "bg-green-100 border-green-300";
  };

  const getStockBarWidth = (ingredientId) => {
    const stock = stockMap[ingredientId];
    if (!stock || !stock.threshold) return "0%";
    
    const percentage = Math.min((stock.currentStock / (stock.threshold * 2)) * 100, 100);
    return `${percentage}%`;
  };

  // ==========================================
  // AUTH CHECK
  // ==========================================
  useEffect(() => {
    if (userRole !== "admin" && userRole !== "manager") {
      navigate("/dashboard");
    }
  }, [userRole, navigate]);

  // ==========================================
  // DATA FETCHING
  // ==========================================
  const fetchIngredients = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.get("/production/ingredients/");
      const formatted = data.map(ing => ({
        ...ing,
        cost_pln: groszyToPln(ing.cost_groszy_per_unit)
      }));
      setIngredients(formatted);
      
      if (formatted.length > 0 && !newSupply.ingredient_id) {
        setNewSupply(prev => ({ ...prev, ingredient_id: formatted[0].id }));
      }
    } catch (err) {
      showError("Failed to load ingredients");
      // Removed console.error
    } finally {
      setLoading(false);
    }
  };

  const fetchSupplies = async () => {
    try {
      const data = await apiClient.get("/supplies/");
      setSupplies(data);
    } catch (err) {
      // Removed console.error
    }
  };

  const fetchStock = async () => {
    try {
      const data = await apiClient.get("/stock/");
      setStockData(data);
    } catch (err) {
      // silent fail - stock will show empty
      // Removed console.error
    }
  };

  useEffect(() => {
    if (userRole === "admin" || userRole === "manager") {
      fetchIngredients();
      fetchSupplies();
      fetchStock(); // ← NEW: fetch real stock
    }
  }, [userRole]);

  // Also refetch stock after any supply or production change
  const refetchAll = () => {
    fetchSupplies();
    fetchStock();
  };

  // ==========================================
  // SUPPLY OPERATIONS
  // ==========================================
  const handleAddSupply = async (e) => {
    e.preventDefault();
    const amountValue = parseFloat(newSupply.amount);
    
    if (!newSupply.ingredient_id || isNaN(amountValue) || amountValue <= 0) {
      showError("Please select ingredient and enter a valid amount");
      return;
    }

    try {
      await apiClient.post("/supplies/", {
        ingredient_id: parseInt(newSupply.ingredient_id),
        amount: amountValue,
        notes: newSupply.notes || null
      });

      showSuccess("✅ Supply added successfully!");
      setNewSupply({ 
        ingredient_id: ingredients[0]?.id || "", 
        amount: "", 
        notes: "" 
      });
      refetchAll(); // ← refresh stock + supplies
    } catch (err) {
      showError("Failed to add supply");
      // Removed console.error
    }
  };

  // ==========================================
  // INGREDIENT OPERATIONS
  // ==========================================
  const handleAddIngredient = async (e) => {
    e.preventDefault();
    
    if (!newIngredient.name.trim() || !newIngredient.unit.trim()) {
      showError("Name and unit are required");
      return;
    }

    try {
      await apiClient.post("/production/ingredients/", {
        name: newIngredient.name.trim(),
        unit: newIngredient.unit.trim(),
        cost_groszy_per_unit: plnToGroszy(newIngredient.cost_pln)
      });

      showSuccess("✅ Ingredient added successfully!");
      setNewIngredient({ name: "", unit: "kg", cost_pln: "" });
      fetchIngredients();
      fetchStock(); // ← new ingredient may affect stock view
    } catch (err) {
      showError("Failed to add ingredient (name may already exist)");
      // Removed console.error
    }
  };

  const handleUpdateIngredient = async (e) => {
    e.preventDefault();

    try {
      await apiClient.put(`/production/ingredients/${editingId}`, {
        name: editForm.name.trim(),
        unit: editForm.unit.trim(),
        cost_groszy_per_unit: plnToGroszy(editForm.cost_pln)
      });

      showSuccess("✅ Ingredient updated successfully!");
      setEditingId(null);
      setEditForm({ name: "", unit: "kg", cost_pln: "" });
      fetchIngredients();
      fetchStock();
    } catch (err) {
      showError("Failed to update ingredient");
      // Removed console.error
    }
  };

  const handleDeleteIngredient = async (id, name) => {
    if (!window.confirm(`⚠️ Delete ingredient "${name}"?\n\nThis cannot be undone if the ingredient is used in production.`)) {
      return;
    }

    try {
      await apiClient.delete(`/production/ingredients/${id}`);
      showSuccess("✅ Ingredient deleted successfully!");
      setIngredients(prev => prev.filter(ing => ing.id !== id));
      fetchStock(); // ← cleanup
    } catch (err) {
      showError("Failed to delete ingredient (may be used in production)");
      // Removed console.error
    }
  };

  const startEdit = (ing) => {
    setEditingId(ing.id);
    setEditForm({
      name: ing.name,
      unit: ing.unit,
      cost_pln: ing.cost_pln
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: "", unit: "kg", cost_pln: "" });
  };

  const getIngredientName = (ingredientId) => {
    const ing = ingredients.find(i => i.id === ingredientId);
    return ing ? `${ing.name} (${ing.unit})` : "Unknown";
  };

  // ==========================================
  // RENDER
  // ==========================================
  if (userRole !== "admin" && userRole !== "manager") return null;

  const isAdmin = userRole === "admin";

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6 bg-[#F5E9D8] min-h-screen">
      {/* Page Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-[#2D2F5E]">🧂 Stock & Ingredient Management</h2>
        <p className="text-sm text-[#2D2F5E] opacity-70 mt-1">
          Manage your bakery ingredients and track supply deliveries
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-100 border border-green-300 text-green-700 p-3 rounded-lg text-center font-medium animate-pulse">
          {success}
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 border border-red-300 text-red-700 p-3 rounded-lg text-center font-medium">
          {error}
        </div>
      )}

      {/* LOW STOCK ALERTS (Admin Only) */}
      {isAdmin && lowStockItems.length > 0 && (
        <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 animate-pulse">
          <div className="flex items-center mb-3">
            <span className="text-2xl mr-2">🚨</span>
            <h3 className="font-bold text-red-800 text-lg">LOW STOCK ALERT!</h3>
          </div>
          <div className="space-y-2">
            {lowStockItems.map((item, idx) => (
              <div key={idx} className="bg-white rounded p-3 border border-red-300">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-red-700">
                    {item.name} ({item.unit})
                  </span>
                  <span className="text-red-800 font-bold">
                    {item.currentStock.toFixed(1)} {item.unit} 
                    <span className="text-sm ml-2">(⚠️ Below {item.threshold} {item.unit})</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CURRENT STOCK STATUS (Admin Only) */}
      {isAdmin && (
        <div className="bg-white rounded-lg shadow-sm border border-[#2D2F5E] border-opacity-20 p-5">
          <h3 className="font-bold text-[#2D2F5E] text-lg mb-4">📊 Current Stock Levels</h3>
          
          {ingredients.length === 0 ? (
            <p className="text-[#2D2F5E] italic text-center py-4">No ingredients to display</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ingredients.map(ing => {
                const stock = stockMap[ing.id];
                if (!stock) return null;

                return (
                  <div
                    key={ing.id}
                    className={`border-2 rounded-lg p-4 ${getStockColor(ing.id)}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-bold text-[#2D2F5E]">{ing.name}</div>
                      {stock.isLow && <span className="text-xl">🚨</span>}
                    </div>
                    
                    <div className="text-2xl font-bold text-[#2D2F5E] mb-1">
                      {stock.currentStock.toFixed(1)} {ing.unit}
                    </div>
                    
                    {stock.threshold && (
                      <>
                        <div className="text-xs text-[#2D2F5E] opacity-70 mb-2">
                          Alert threshold: {stock.threshold} {ing.unit}
                        </div>
                        
                        {/* Visual Bar */}
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              stock.isLow 
                                ? "bg-red-500" 
                                : stock.currentStock < stock.threshold * 1.5
                                ? "bg-yellow-500"
                                : "bg-green-500"
                            }`}
                            style={{ width: getStockBarWidth(ing.id) }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-[#2D2F5E] border-opacity-20 overflow-hidden">
        <div className="flex border-b border-[#2D2F5E] border-opacity-20">
          <button
            onClick={() => setActiveTab("supplies")}
            className={`flex-1 px-6 py-3 font-medium transition ${
              activeTab === "supplies"
                ? "bg-[#2D2F5E] text-white"
                : "bg-[#F5E9D8] text-[#2D2F5E] hover:bg-[#E9DCC8]"
            }`}
          >
            📦 Supply History ({supplies.length})
          </button>
          <button
            onClick={() => setActiveTab("ingredients")}
            className={`flex-1 px-6 py-3 font-medium transition ${
              activeTab === "ingredients"
                ? "bg-[#2D2F5E] text-white"
                : "bg-[#F5E9D8] text-[#2D2F5E] hover:bg-[#E9DCC8]"
            }`}
          >
            🧂 Ingredients ({ingredients.length})
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-5">
          {activeTab === "supplies" && (
            <div className="space-y-6">
              {/* Add New Supply Form */}
              <div className="bg-[#F5E9D8] p-4 rounded-lg border border-[#2D2F5E] border-opacity-20">
                <h3 className="font-bold text-[#2D2F5E] mb-3 flex items-center">
                  <span className="mr-2">📦</span>
                  Record New Supply Delivery
                </h3>
                <form onSubmit={handleAddSupply} className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <select
                      value={newSupply.ingredient_id}
                      onChange={(e) => setNewSupply({...newSupply, ingredient_id: e.target.value})}
                      className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                      required
                    >
                      <option value="">Select Ingredient</option>
                      {ingredients.map(ing => (
                        <option key={ing.id} value={ing.id}>
                          {ing.name} ({ing.unit})
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      placeholder="Amount"
                      value={newSupply.amount}
                      onChange={(e) => setNewSupply({...newSupply, amount: e.target.value})}
                      className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                      required
                    />
                  </div>
                  <textarea
                    placeholder="Notes (optional) - e.g., supplier name, batch number"
                    value={newSupply.notes}
                    onChange={(e) => setNewSupply({...newSupply, notes: e.target.value})}
                    className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                    rows="2"
                  />
                  <button
                    type="submit"
                    className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition shadow font-medium"
                  >
                    ✅ Record Supply
                  </button>
                </form>
              </div>

              {/* Supply History */}
              <div>
                <h3 className="font-bold text-[#2D2F5E] mb-3">📋 Supply History (Recent First)</h3>
                {supplies.length === 0 ? (
                  <div className="bg-white p-8 rounded-lg border border-[#2D2F5E] border-opacity-20 text-center">
                    <p className="text-[#2D2F5E] italic">No supply records yet. Add your first supply above!</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {supplies
                      .slice()
                      .sort((a, b) => new Date(b.delivered_at) - new Date(a.delivered_at))
                      .map(supply => (
                        <div
                          key={supply.id}
                          className="bg-white border border-[#2D2F5E] border-opacity-20 p-3 rounded-lg hover:shadow-sm transition"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-medium text-[#2D2F5E]">
                                {getIngredientName(supply.ingredient_id)}
                              </div>
                              <div className="text-sm text-[#2D2F5E] opacity-70 mt-1">
                                📅 {formatDate(supply.delivered_at)}
                              </div>
                              {supply.notes && (
                                <div className="text-sm text-[#2D2F5E] mt-1 italic">
                                  📝 {supply.notes}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-green-700">
                                +{supply.amount}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "ingredients" && (
            <div className="space-y-6">
              {/* Add New Ingredient Form */}
              <div className="bg-[#F5E9D8] p-4 rounded-lg border border-[#2D2F5E] border-opacity-20">
                <h3 className="font-bold text-[#2D2F5E] mb-3 flex items-center">
                  <span className="mr-2">➕</span>
                  Add New Ingredient
                </h3>
                <form onSubmit={handleAddIngredient} className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <input
                      type="text"
                      placeholder="Name (e.g., Flour)"
                      value={newIngredient.name}
                      onChange={(e) => setNewIngredient({...newIngredient, name: e.target.value})}
                      className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Unit (kg, l, pcs)"
                      value={newIngredient.unit}
                      onChange={(e) => setNewIngredient({...newIngredient, unit: e.target.value})}
                      className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                      required
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Cost per unit (PLN)"
                      value={newIngredient.cost_pln}
                      onChange={(e) => setNewIngredient({...newIngredient, cost_pln: e.target.value})}
                      className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full sm:w-auto bg-[#2D2F5E] hover:bg-[#1D1F4E] text-white px-6 py-2 rounded-lg transition shadow font-medium"
                  >
                    ➕ Add Ingredient
                  </button>
                </form>
              </div>

              {/* Ingredients List */}
              <div>
                <h3 className="font-bold text-[#2D2F5E] mb-3">📋 Manage Ingredients</h3>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D2F5E]"></div>
                    <p className="text-[#2D2F5E] mt-2">Loading ingredients...</p>
                  </div>
                ) : ingredients.length === 0 ? (
                  <div className="bg-white p-8 rounded-lg border border-[#2D2F5E] border-opacity-20 text-center">
                    <p className="text-[#2D2F5E] italic">No ingredients yet. Add your first ingredient above!</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {ingredients.map(ing => (
                      <div
                        key={ing.id}
                        className="bg-white border border-[#2D2F5E] border-opacity-20 p-4 rounded-lg hover:shadow-md transition"
                      >
                        {editingId === ing.id ? (
                          <form onSubmit={handleUpdateIngredient} className="space-y-2">
                            <input
                              type="text"
                              value={editForm.name}
                              onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                              className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E]"
                              placeholder="Name"
                              required
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="text"
                                value={editForm.unit}
                                onChange={(e) => setEditForm({...editForm, unit: e.target.value})}
                                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E]"
                                placeholder="Unit"
                                required
                              />
                              <input
                                type="number"
                                step="0.01"
                                value={editForm.cost_pln}
                                onChange={(e) => setEditForm({...editForm, cost_pln: e.target.value})}
                                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E]"
                                placeholder="Cost (PLN)"
                              />
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="submit"
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded font-medium"
                              >
                                ✅ Save
                              </button>
                              <button
                                type="button"
                                onClick={cancelEdit}
                                className="flex-1 bg-gray-300 hover:bg-gray-400 text-[#2D2F5E] py-2 rounded font-medium"
                              >
                                ❌ Cancel
                              </button>
                            </div>
                          </form>
                        ) : (
                          <div>
                            <div className="font-bold text-[#2D2F5E] text-lg mb-2">{ing.name}</div>
                            <div className="space-y-1 text-sm text-[#2D2F5E] mb-3">
                              <div className="flex justify-between">
                                <span className="opacity-70">📏 Unit:</span>
                                <span className="font-medium">{ing.unit}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="opacity-70">💰 Cost:</span>
                                <span className="font-bold text-green-700">{ing.cost_pln} PLN</span>
                              </div>
                              {isAdmin && stockMap[ing.id] && (
                                <div className="flex justify-between pt-2 border-t border-[#2D2F5E] border-opacity-10">
                                  <span className="opacity-70">📊 Stock:</span>
                                  <span className="font-bold">
                                    {stockMap[ing.id].currentStock.toFixed(1)} {ing.unit}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 pt-3 border-t border-[#2D2F5E] border-opacity-10">
                              <button
                                onClick={() => startEdit(ing)}
                                className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 rounded text-sm font-medium transition"
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={() => handleDeleteIngredient(ing.id, ing.name)}
                                className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 py-2 rounded text-sm font-medium transition"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockManagement;